import React, { useState, useEffect, useCallback } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
  Input,
} from "reactstrap"
import ReactApexChart from "react-apexcharts"
import axios from "axios"
import { useSelector } from "react-redux"
import config from "config/config"

const PLATFORMS = ["Facebook", "Healthgrade", "Yelp"]

const MonthlyEarnings = ({ viewMode, fromDate, toDate }) => {
  console.log(viewMode, fromDate, toDate, "fromdate and todate in monthly earnings")
  const [selectedPlatforms, setSelectedPlatforms] = useState([...PLATFORMS])
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [cumulativeReviews, setCumulativeReviews] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const [chartData, setChartData] = useState({
    options: {
      chart: { stacked: false, toolbar: { show: false } },
      dataLabels: { enabled: false },
      plotOptions: { bar: { columnWidth: "60%" } },
      stroke: { width: [0, 3] },
      markers: { size: 5 },
      xaxis: {
        categories: [],
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { formatter: val => val.toFixed(0) } },
      fill: { opacity: 1 },
      grid: { borderColor: "#f8f8fa" },
      legend: { position: "top" },
    },
    series: [
      { name: "Reviews", type: "bar", data: [] },
      { name: "Trend", type: "line", data: [] },
    ],
  })

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen)

  const fetchReviews = useCallback(async () => {
    if (!activeOrganization) return

    try {
      let allReviews = []

      if (activeOrganization?.FacebookIntegrations[0]) {
        const response = await axios.get(
          `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/ratings`,
          {
            params: {
              access_token:
                activeOrganization.FacebookIntegrations[0].pageToken,
              fields:
                "id,reviewer{name,picture},created_time,recommendation_type,review_text",
            },
          },
        )
        allReviews.push(
          ...response.data.data.map(r => ({ ...r, platform: "Facebook" })),
        )
      }

      if (activeOrganization?.HealthgradeIntegrations[0]) {
        const response = await axios.get(
          `${config.backendUrl}/healthgrade/getReviews?integrationId=${activeOrganization.HealthgradeIntegrations[0].id}`,
          { headers: { Authorization: `Bearer ${userData.token}` } },
        )

        console.log("reviews", response.data.reviews)
        allReviews.push(
          ...response.data.reviews.map(r => ({
            ...r,
            platform: "Healthgrade",
          })),
        )
      }

      if (activeOrganization?.YelpIntegrations[0]) {
        const response = await axios.get(
          `${config.backendUrl}/yelp/getReviews?integrationId=${activeOrganization.YelpIntegrations[0].id}`,
          { headers: { Authorization: `Bearer ${userData.token}` } },
        )
        allReviews.push(
          ...response.data.reviews.map(r => ({ ...r, platform: "Yelp" })),
        )
      }

      console.log("All Reviews", allReviews)

      setCumulativeReviews(allReviews)
      setIsLoading(false)
    } catch (err) {
      console.error("Error fetching reviews:", err)
      setIsLoading(false)
    }
  }, [activeOrganization, userData.token])

  // ✅ Determine filtering range based on `viewMode`
  const determineDateRange = useCallback(() => {
    const now = new Date()
    let start, end

    if (viewMode === "Month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1)
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    } else if (viewMode === "Week") {
      const dayOfWeek = now.getDay() // 0 = Sunday, 1 = Monday, etc.
      start = new Date(now)
      start.setDate(now.getDate() - dayOfWeek) // Start of the current week (Sunday)
      end = new Date(now)
      end.setDate(start.getDate() + 6) // End of the current week (Saturday)
    } else {
      start = fromDate ? new Date(fromDate) : null
      end = toDate ? new Date(toDate) : null
    }

    return { start, end }
  }, [viewMode, fromDate, toDate])

  // ✅ Filter reviews by viewMode or custom date range
  const filterReviewsByDate = useCallback(() => {
    const { start, end } = determineDateRange()
    if (!start || !end) return cumulativeReviews // No filtering if dates are missing

    const from = start.setHours(0, 0, 0, 0)
    const to = end.setHours(23, 59, 59, 999)

    console.log("Start", start)
    console.log("End", end)

    return cumulativeReviews.filter(review => {
      let reviewDate
      if (review.platform === "Facebook") {
        reviewDate = new Date(review.created_time).getTime()
      } else {
        reviewDate = new Date(review.reviewDate).getTime()
      }

      return reviewDate >= from && reviewDate <= to
    })
  }, [cumulativeReviews, determineDateRange])

  const handlePlatformChange = platform => {
    setSelectedPlatforms(prevSelected =>
      prevSelected.includes(platform)
        ? prevSelected.filter(item => item !== platform)
        : [...prevSelected, platform],
    )
  }

  // ✅ Apply filters (Date + Platform)
  const applyFilters = useCallback(() => {
    let filteredReviews = filterReviewsByDate().filter(review =>
      selectedPlatforms.includes(review.platform),
    )

    const aggregatedData = selectedPlatforms.map(
      platform =>
        filteredReviews.filter(review => review.platform === platform).length,
    )

    // ✅ Make trend line match bar tops
    const trendData = [...aggregatedData]

    setChartData(prevData => ({
      ...prevData,
      series: [
        { name: "Reviews", type: "bar", data: aggregatedData },
        { name: "Trend", type: "line", data: trendData },
      ],
      options: {
        ...prevData.options,
        xaxis: { ...prevData.options.xaxis, categories: selectedPlatforms },
      },
    }))
  }, [filterReviewsByDate, selectedPlatforms])

  useEffect(() => {
    fetchReviews()
  }, [fetchReviews, viewMode, fromDate, toDate])

  useEffect(() => {
    applyFilters()
  }, [
    selectedPlatforms,
    cumulativeReviews,
    viewMode,
    fromDate,
    toDate,
    applyFilters,
  ])

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="card-title">Platform Review Counts</h4>
          <div className="d-flex align-items-center">
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
              <DropdownToggle tag="button" className="btn btn-secondary">
                {selectedPlatforms.length === PLATFORMS.length
                  ? "All Platforms"
                  : selectedPlatforms.join(", ")}{" "}
                <span className="mdi mdi-filter-menu"></span>
              </DropdownToggle>
              <DropdownMenu>
                {PLATFORMS.map(platform => (
                  <DropdownItem key={platform} toggle={false}>
                    <Input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform)}
                      onChange={() => handlePlatformChange(platform)}
                    />{" "}
                    {platform}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Row className="text-center mt-4 justify-content-center">
              {selectedPlatforms.map(platform => {
                const filteredCount = filterReviewsByDate().filter(
                  review => review.platform === platform,
                ).length

                return (
                  <Col xs="4" key={platform}>
                    <h5 className="font-size-20">{filteredCount}</h5>
                    <p className="text-muted">{platform}</p>
                  </Col>
                )
              })}
            </Row>
            <ReactApexChart
              options={chartData.options}
              series={chartData.series}
              type="line"
              height="290"
            />
          </>
        )}
      </CardBody>
    </Card>
  )
}

export default MonthlyEarnings
